(() => {
  const SELECTORS = {
    section: '.logos-cc',
    viewport: '[data-carousel-viewport]',
    dots: '[data-carousel-dots]',
    card: '[data-product-card]',
    productJson: '[data-product-json]',
    sizePill: '[data-size-pill]',
    atc: '[data-atc]',
    price: '[data-price]',
    status: '[data-status]',
    qtyMinus: '[data-qty-minus]',
    qtyPlus: '[data-qty-plus]',
    qtyInput: '[data-qty-input]',
  };

  const money = (cents) => {
    const value = (cents / 100).toFixed(2);
    return `$${value}`;
  };

  function getSectionRootById(id) {
    return document.getElementById(`logos-cc-${id}`);
  }

  function parseProductData(card) {
    const script = card.querySelector(SELECTORS.productJson);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function findVariantBySize(productData, sizeOptionName, sizeValue) {
    if (!productData) return null;
    const optIndex = productData.options.findIndex((o) => o === sizeOptionName);
    if (optIndex === -1) return null;

    return productData.variants.find((v) => v.options?.[optIndex] === sizeValue) || null;
  }

  function updatePrice(card, variant) {
    const priceWrap = card.querySelector(SELECTORS.price);
    if (!priceWrap || !variant) return;

    const amountEl =
      priceWrap.querySelector('.price-item--regular') ||
      priceWrap.querySelector('.price-item') ||
      priceWrap.querySelector('[data-regular-price]');

    if (amountEl) {
      amountEl.textContent = money(variant.price);
      return;
    }

    priceWrap.textContent = money(variant.price);
  }

  function setStatus(card, msg) {
    const el = card.querySelector(SELECTORS.status);
    if (el) el.textContent = msg || '';
  }

  function setSelectedPill(card, pill) {
    card.querySelectorAll(SELECTORS.sizePill).forEach((p) => p.setAttribute('aria-pressed', 'false'));
    if (pill) pill.setAttribute('aria-pressed', 'true');
  }

  function getQty(card) {
    const input = card.querySelector(SELECTORS.qtyInput);
    const val = parseInt(input?.value || '1', 10);
    return Number.isFinite(val) && val > 0 ? val : 1;
  }

  function setQty(card, qty) {
    const input = card.querySelector(SELECTORS.qtyInput);
    if (input) input.value = String(Math.max(1, qty));
  }

  /* -----------------------------
     Cart drawer refresh (Taste/Dawn)
  ----------------------------- */

  function replaceShopifySection(sectionName, html) {
    const wrapperId = `shopify-section-${sectionName}`;
    const existing = document.getElementById(wrapperId);
    if (!existing) return false;

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const incomingWrapper = temp.querySelector(`#${CSS.escape(wrapperId)}`);
    if (incomingWrapper) {
      existing.replaceWith(incomingWrapper);
      return true;
    }

    // If response is unwrapped, replace inside
    existing.innerHTML = html;
    return true;
  }

  async function refreshCartUIFromSections(sections) {
    const names = ['cart-icon-bubble', 'cart-drawer'];

    names.forEach((name) => {
      const html = sections?.[name];
      if (html) replaceShopifySection(name, html);
    });

    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.remove('is-empty');

    // Prefer theme API if present
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents({ sections });
    }
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  async function addToCart(variantId, quantity) {
    const sections = ['cart-drawer', 'cart-icon-bubble'];

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity }],
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const data = await res.json();

    if (!res.ok || data?.status) {
      const msg = data?.description || data?.message || 'Could not add to cart.';
      throw new Error(msg);
    }

    if (data.sections) {
      await refreshCartUIFromSections(data.sections);
    } else {
      // Fallback: open drawer anyway
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }

    return data;
  }

  /* -----------------------------
     Carousel + cards
  ----------------------------- */

  function initCarousel(root) {
    const viewport = root.querySelector(SELECTORS.viewport);
    const dotsWrap = root.querySelector(SELECTORS.dots);
    if (!viewport) return;

    const cards = Array.from(viewport.children).filter((el) => el.matches('.logos-cc__card'));
    if (!cards.length) return;

    const prevBtn = root.querySelector('[data-action="prev"]');
    const nextBtn = root.querySelector('[data-action="next"]');

    const scrollByOne = (dir) => {
      const firstCard = cards[0];
      if (!firstCard) return;
      const style = getComputedStyle(viewport);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      const amount = firstCard.getBoundingClientRect().width + gap;
      viewport.scrollBy({ left: dir * amount, behavior: 'smooth' });
    };

    prevBtn?.addEventListener('click', () => scrollByOne(-1));
    nextBtn?.addEventListener('click', () => scrollByOne(1));

    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      const dotCount = Math.max(1, cards.length);

      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'logos-cc__dot';
        dot.setAttribute('aria-label', `Go to item ${i + 1}`);
        dot.addEventListener('click', () => {
          cards[i]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
        dotsWrap.appendChild(dot);
      }

      const setActiveDot = () => {
        const dots = Array.from(dotsWrap.querySelectorAll('.logos-cc__dot'));
        let active = 0;
        const vpLeft = viewport.getBoundingClientRect().left;
        let best = Infinity;

        cards.forEach((c, idx) => {
          const dist = Math.abs(c.getBoundingClientRect().left - vpLeft);
          if (dist < best) {
            best = dist;
            active = idx;
          }
        });

        dots.forEach((d, idx) => d.setAttribute('aria-current', idx === active ? 'true' : 'false'));
      };

      viewport.addEventListener('scroll', () => window.requestAnimationFrame(setActiveDot));
      setActiveDot();
    }
  }

  function initCard(root, card) {
    const productData = parseProductData(card);
    const sizeOptionName = root.dataset.sizeOptionName || 'Size';
    const atcBtn = card.querySelector(SELECTORS.atc);

    // qty controls
    card.querySelector(SELECTORS.qtyMinus)?.addEventListener('click', () => setQty(card, getQty(card) - 1));
    card.querySelector(SELECTORS.qtyPlus)?.addEventListener('click', () => setQty(card, getQty(card) + 1));
    card.querySelector(SELECTORS.qtyInput)?.addEventListener('change', () => setQty(card, getQty(card)));

    const pills = Array.from(card.querySelectorAll(SELECTORS.sizePill));
    const firstAvailable = pills.find((p) => !p.disabled) || null;

    let selectedVariant = null;

    const selectSize = (pill) => {
      if (!pill || pill.disabled) return;

      const sizeValue = pill.dataset.sizeValue;
      const variant = findVariantBySize(productData, sizeOptionName, sizeValue);

      if (!variant) {
        setStatus(card, 'Size not available.');
        if (atcBtn) atcBtn.disabled = true;
        selectedVariant = null;
        return;
      }

      selectedVariant = variant;
      setSelectedPill(card, pill);
      updatePrice(card, variant);

      if (atcBtn) atcBtn.disabled = !variant.available;
      setStatus(card, '');
    };

    pills.forEach((pill) => pill.addEventListener('click', () => selectSize(pill)));
    if (firstAvailable) selectSize(firstAvailable);

    atcBtn?.addEventListener('click', async () => {
      if (!selectedVariant?.id) return;

      const qty = getQty(card);

      atcBtn.disabled = true;
      setStatus(card, 'Adding…');

      try {
        await addToCart(selectedVariant.id, qty);
        setStatus(card, 'Added to cart');
        setTimeout(() => setStatus(card, ''), 1200);
      } catch (e) {
        setStatus(card, e.message || 'Could not add to cart.');
      } finally {
        if (selectedVariant?.available) atcBtn.disabled = false;
      }
    });
  }

  function initSection(root) {
    if (!root || root.dataset.logosCcInit === 'true') return;
    root.dataset.logosCcInit = 'true';

    initCarousel(root);

    const cards = Array.from(root.querySelectorAll(SELECTORS.card));
    cards.forEach((card) => initCard(root, card));
  }

  function boot() {
    document.querySelectorAll(SELECTORS.section).forEach(initSection);

    if (window.LOGOS_CC_SECTIONS?.length) {
      window.LOGOS_CC_SECTIONS.forEach(({ id }) => {
        const root = getSectionRootById(id);
        if (root) initSection(root);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target?.querySelector?.(SELECTORS.section) || e.target?.closest?.(SELECTORS.section);
    if (root) initSection(root);
  });
})();
